import React, { useEffect, useMemo, useRef, useState } from "react";
import "./FAQ.css";

/**
 * Props:
 * - faqs: [{ q: string, a: string }]
 * - allowMultiple: boolean (default true) — allow multiple items open at once
 * - onCtaClick: function for the footer CTA button
 */
export default function FAQ({
  faqs,
  allowMultiple = true,
  onCtaClick = () => (window.location.href = "/contact"),
}) {
  const items = useMemo(
    () =>
      faqs ?? [
        {
          q: "What types of properties do you appraise?",
          a: "We specialize in residential properties, including single-family homes, condominiums, townhomes, manufactured homes, and 2–4 unit multi-family properties.",
        },
        {
          q: "Which areas do you serve?",
          a: "We provide appraisals across the Greater Puget Sound: Pierce, King, Thurston, Lewis, Mason, Kitsap, Grays Harbor and Snohomish counties.",
        },
        {
          q: "Are you FHA approved?",
          a: "Yes. Emerald Appraisal Services is FHA-approved, and our reports are USPAP-compliant and lender-ready.",
        },
        {
          q: "How long does an appraisal take?",
          a: "Typical turnaround is 3–5 business days after the on-site inspection. Complex assignments and peak seasons may require additional time. Rush service is available.",
        },
        {
          q: "Do you only appraise for lenders?",
          a: "No. We also complete appraisals for estate planning and date-of-death (retrospective), divorce, pre-listing, tax assessment review, and personal valuation.",
        },
        {
          q: "What should I expect during the inspection?",
          a: "We take interior and exterior photos, measure the home per ANSI standards, and note features, updates, and overall condition. Most inspections take 30 to 60 minutes depending on the property.",
        },
        {
          q: "Can you explain how value is determined?",
          a: "We analyze recent comparable sales, market trends, location influences, and the home's characteristics. Adjustments are applied to arrive at a well-supported opinion of value.",
        },
        {
          q: "Do you provide a copy of the report?",
          a: "Yes. You'll receive a PDF of the completed report from us or your lender, depending on the assignment. We're happy to clarify any part of the analysis.",
        },
      ],
    [faqs]
  );

  const [open, setOpen] = useState(() => new Set());
  const btnRefs = useRef([]);

  const toggle = (idx) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else {
        if (!allowMultiple) next.clear();
        next.add(idx);
      }
      return next;
    });
  };

  // keyboard navigation among question buttons
  const onKeyDown = (e, idx) => {
    const total = items.length;
    const focusBtn = (i) => btnRefs.current[i]?.focus();
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusBtn((idx + 1) % total);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusBtn((idx - 1 + total) % total);
        break;
      case "Home":
        e.preventDefault();
        focusBtn(0);
        break;
      case "End":
        e.preventDefault();
        focusBtn(total - 1);
        break;
      default:
        break;
    }
  };

  return (
    <section className="FAQ" aria-labelledby="faq-title">
      <div className="FAQ__container">
        <header className="FAQ__header">
          <p className="FAQ__eyebrow">FAQ</p>
          <h2 id="faq-title" className="FAQ__title">
            Frequently Asked Questions
          </h2>
          <p className="FAQ__sub">
            Quick answers about our appraisal process, coverage, and reports.
          </p>
        </header>

        <ul className="FAQ__list">
          {items.map((item, i) => (
            <FAQItem
              key={i}
              index={i}
              question={item.q}
              answer={item.a}
              open={open.has(i)}
              onToggle={() => toggle(i)}
              onKeyDown={(e) => onKeyDown(e, i)}
              btnRef={(el) => (btnRefs.current[i] = el)}
            />
          ))}
        </ul>

        <div className="FAQ__footer">
          <button className="FAQ__cta" onClick={onCtaClick}>
            Still have questions? Contact us
            <svg
              className="FAQ__cta-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}

function FAQItem({ index, question, answer, open, onToggle, onKeyDown, btnRef }) {
  const panelRef = useRef(null);

  // Smooth height transition
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (open) {
      el.style.maxHeight = el.scrollHeight + "px";
    } else {
      el.style.maxHeight = "0px";
    }
  }, [open]);

  const panelId = `faq-panel-${index}`;
  const btnId = `faq-button-${index}`;

  return (
    <li className={`FAQ__item ${open ? "is-open" : ""}`}>
      <button
        id={btnId}
        ref={btnRef}
        className="FAQ__question"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        onKeyDown={onKeyDown}
        type="button"
      >
        <span className="FAQ__qtext">{question}</span>
        <span className="FAQ__chev" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M5 7l5 6 5-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <div
        id={panelId}
        role="region"
        aria-labelledby={btnId}
        className="FAQ__answer"
        ref={panelRef}
      >
        <p>{answer}</p>
      </div>
    </li>
  );
}
